import React, { useContext, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, Link, Typography } from '@telus/koodo-ui-kit'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import { logOut } from '@apps/redux/features/CommonSlice'
import { getById, getBySlug } from '@apps/utils/contentful'
import DealerActivationPaths from '@sites/dealer/activation/DealerActivationPaths'
import Sites from '@sites/index'

const DealerSessionCard = () => {
  const dispatch = useDispatch()

  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language,
    [APPLICATION_STATE.IDENTITY]: identity,
    [APPLICATION_STATE.SERVICES]: brandServices
  } = useContext(ApplicationContext)

  const {
    userType,
    user: { isLoggedIn },
    content: { pageContent }
  } = useSelector((state) => state.common)

  const isDealer = userType === Sites.dealer

  // Construct during render — the path class calls `useLocation()` in its
  // constructor chain, so `new DealerActivationPaths()` inside an event
  // handler triggers "Invalid hook call". Same pattern as ConsumerSessionCard.
  const dealerActivationPaths = new DealerActivationPaths()

  const dealerText = useMemo(() => {
    if (!pageContent || !language) return {}
    const commonElements = getById(pageContent, 'commonElements')?.[language]
    const shortTextFields = commonElements?.shortTextFields
    return {
      repName: getBySlug(shortTextFields, 'header-repName')?.value || 'Sales Rep:',
      channelorgCode: getBySlug(shortTextFields, 'header-channelorg-code')?.value || 'Channel Org Code:',
      needAssistance: getBySlug(shortTextFields, 'header-needAssistance')?.value || 'Need Assistance?',
      needAssistanceUrl: getBySlug(shortTextFields, 'header-need-assistance-url')?.value,
      callSupport: getBySlug(shortTextFields, 'header-callSupport')?.value || 'Call Support:',
      callSupportPhoneNumber: getBySlug(shortTextFields, 'header-call-support-phonenumber')?.value,
      logoutLabel: getBySlug(shortTextFields, 'header-logout')?.value || 'Logout'
    }
  }, [pageContent, language])

  const dealerIdentity = useMemo(() => {
    if (!identity) {
      return { salesRepName: '', channelOrgCode: '' }
    }
    const firstName = identity.givenName
      ? `${identity.givenName.charAt(0).toUpperCase()}${identity.givenName.slice(1)}`
      : ''
    const lastInitial = identity.sn ? `${identity.sn.charAt(0).toUpperCase()}.` : ''
    return {
      salesRepName: [firstName, lastInitial].filter(Boolean).join(' '),
      channelOrgCode: identity.channelOrgCode || ''
    }
  }, [identity])

  const handleLogout = async () => {
    const { retrieveCookie, deleteCookie } = brandServices || {}
    const session = (retrieveCookie && (await retrieveCookie())) || {}
    if (session.userSessionId) {
      await deleteCookie()
    }
    dispatch(logOut())
    window.location.href = dealerActivationPaths.Base
  }

  const handleLogoutClick = (event) => {
    event.preventDefault()
    handleLogout()
  }

  const handleSessionCardKeyDown = (event) => {
    if (event.defaultPrevented || event.repeat) return
    if (!['Enter', ' ', 'Spacebar'].includes(event.key)) return

    event.preventDefault()
    event.stopPropagation()
    handleLogout()
  }

  const handleNeedAssistanceClick = (event) => {
    if (!dealerText.needAssistanceUrl) return
    event.preventDefault()
    // `noopener,noreferrer` prevents the opened popup from accessing
    // window.opener (tabnabbing).
    window.open(
      dealerText.needAssistanceUrl,
      '_blank',
      'noopener,noreferrer,scrollbars=no,resizable=no,status=no,location=no,toolbar=no,personalbar=no,origin=no,menubar=no,width=400,height=725'
    )
  }

  if (!isDealer) return null

  // Single kit `Card` for both pre- and post-login states so padding stays
  // consistent. Logout is rendered inline so it can be vertically centered
  // against the two-line body via flexbox `items-center`, matching the design.
  return (
    <Card
      testId="dealer-session-card"
      variant={{ borderRadius: 'large', padding: 'intermediate' }}
      tokens={{ paddingTop: 24, paddingBottom: 24 }}
    >
      <div className="flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
        <div className="flex flex-col gap-1">
          <Typography>
            {dealerText.repName}
            {' '}
            <span className="text-[#7a3dfc] font-semibold">{dealerIdentity.salesRepName}</span>
            {' / '}
            {dealerText.channelorgCode}
            {' '}
            <span className="text-[#7a3dfc] font-semibold">{dealerIdentity.channelOrgCode}</span>
          </Typography>
          <Typography>
            <Link
              href={dealerText.needAssistanceUrl}
              variant={{ bold: true }}
              tokens={{
                blockFontSize: 16,
                blockLineHeight: 1.5,
                color: 'var(--color-copy)'
              }}
              onClick={handleNeedAssistanceClick}
            >
              {dealerText.needAssistance}
            </Link>
            {' '}
            {dealerText.callSupport}
            {' '}
            {dealerText.callSupportPhoneNumber && (
              <>
                <br className="md:hidden" />
                <Typography variant={{ bold: true }}>
                  {dealerText.callSupportPhoneNumber}
                </Typography>
              </>
            )}
          </Typography>
        </div>
        {isLoggedIn && (
          <div
            className="md:self-center shrink-0 min-w-max whitespace-nowrap [&_*]:!whitespace-nowrap"
            onKeyDownCapture={handleSessionCardKeyDown}
          >
            {/* Kit `Link` styled as a button — onClick triggers logout. */}
            <Link
              accessibilityRole="button"
              href={dealerActivationPaths.Base}
              onClick={handleLogoutClick}
              tokens={{
                blockFontName: null,
                blockFontSize: 16,
                blockFontWeight: '700',
                blockLineHeight: 1.5,
                color: 'var(--color-copy)'
              }}
            >
              {dealerText.logoutLabel}
            </Link>
          </div>
        )}
      </div>
    </Card>
  )
}

export default DealerSessionCard
